import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Star, Clock, Users, ArrowRight, BookOpen, Award, TrendingUp, Check } from 'lucide-react';
import { MOCK_CATEGORIES, MOCK_COURSES } from '../utils/mockData';

function HomePage() {
  const [categories, setCategories] = useState([]);
  const [featuredCourses, setFeaturedCourses] = useState([]);
  const [trendingCourses, setTrendingCourses] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    // Simulate loading delay
    setTimeout(() => {
      setCategories(MOCK_CATEGORIES);
      setFeaturedCourses(MOCK_COURSES.slice(0, 6));
      setTrendingCourses(MOCK_COURSES.slice(0, 8));
    }, 500);
  };

  const fadeUp = {
    initial: { opacity: 0, y: 20 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.5 },
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="section-padding pt-32 bg-gradient-to-br from-slate-900 via-slate-800 to-blue-900">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            {/* Left Content */}
            <motion.div className="lg:col-span-7" {...fadeUp}>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white mb-6 leading-tight">
                Learn Without Limits
              </h1>
              <p className="text-lg text-slate-300 mb-8 leading-relaxed max-w-2xl">
                Master new skills with expert-led courses. Build your future with Lumina's world-class online learning platform.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/courses" data-testid="explore-courses-button">
                  <button className="bg-blue-600 text-white hover:bg-blue-700 rounded-full px-8 py-4 text-lg font-semibold shadow-lg hover:shadow-blue-500/25 transition-all duration-300">
                    Explore Courses
                  </button>
                </Link>
                <Link to="/register" data-testid="get-started-button">
                  <button className="bg-white text-slate-900 hover:bg-slate-50 rounded-full px-8 py-4 text-lg font-semibold transition-all duration-300">
                    Get Started Free
                  </button>
                </Link>
              </div>
              {/* Stats */}
              <div className="grid grid-cols-3 gap-6 mt-12">
                <div>
                  <div className="text-3xl font-extrabold text-white">10K+</div>
                  <div className="text-slate-300 text-sm">Active Students</div>
                </div>
                <div>
                  <div className="text-3xl font-extrabold text-white">500+</div>
                  <div className="text-slate-300 text-sm">Expert Courses</div>
                </div>
                <div>
                  <div className="text-3xl font-extrabold text-white">95%</div>
                  <div className="text-slate-300 text-sm">Success Rate</div>
                </div>
              </div>
            </motion.div>

            {/* Right Image */}
            <motion.div
              className="lg:col-span-5"
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="relative">
                <img
                  src="https://images.unsplash.com/photo-1758874384930-6e1452bb9c71?crop=entropy&cs=srgb&fm=jpg&q=85"
                  alt="Learning"
                  className="rounded-3xl shadow-2xl w-full"
                />
                <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl p-6 shadow-xl">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                      <Award className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-slate-900">4.9/5</div>
                      <div className="text-sm text-slate-600">Student Rating</div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Categories Section */}
      <section className="section-padding bg-stone-50">
        <div className="max-w-7xl mx-auto container-padding">
          <motion.div className="text-center mb-16" {...fadeUp}>
            <h2 className="text-4xl font-extrabold text-slate-900 mb-4">Explore by Category</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Choose from hundreds of courses across popular categories
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {categories.slice(0, 8).map((category, index) => (
              <motion.div
                key={category.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
              >
                <Link to={`/courses?category=${category.id}`} data-testid={`category-${category.id}`}>
                  <div className="bg-white rounded-2xl p-8 border border-slate-100 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 group">
                    <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center mb-4 group-hover:bg-blue-600 transition-colors">
                      <BookOpen className="w-7 h-7 text-blue-600 group-hover:text-white transition-colors" />
                    </div>
                    <h3 className="text-xl font-bold text-slate-900 mb-2">{category.name}</h3>
                    <p className="text-slate-600 text-sm">{category.description}</p>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Courses Section */}
      <section className="section-padding bg-white">
        <div className="max-w-7xl mx-auto container-padding">
          <motion.div className="flex items-center justify-between mb-12" {...fadeUp}>
            <div>
              <h2 className="text-4xl font-extrabold text-slate-900 mb-2">Featured Courses</h2>
              <p className="text-lg text-slate-600">Top-rated courses selected by our experts</p>
            </div>
            <Link to="/courses" data-testid="view-all-featured">
              <button className="hidden md:flex items-center gap-2 text-blue-600 hover:text-blue-700 font-semibold">
                View All <ArrowRight className="w-5 h-5" />
              </button>
            </Link>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {featuredCourses.slice(0, 6).map((course, index) => (
              <CourseCard key={course.id} course={course} index={index} />
            ))}
          </div>
        </div>
      </section>

      {/* Trending Courses */}
      <section className="section-padding bg-stone-50">
        <div className="max-w-7xl mx-auto container-padding">
          <motion.div className="text-center mb-12" {...fadeUp}>
            <div className="inline-flex items-center gap-2 bg-amber-100 text-amber-700 px-4 py-2 rounded-full text-sm font-semibold mb-4">
              <TrendingUp className="w-4 h-4" />
              Trending Now
            </div>
            <h2 className="text-4xl font-extrabold text-slate-900 mb-4">Most Popular Courses</h2>
            <p className="text-lg text-slate-600">Join thousands of students learning these courses</p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {trendingCourses.slice(0, 8).map((course, index) => (
              <CourseCard key={course.id} course={course} index={index} compact />
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="section-padding bg-white">
        <div className="max-w-7xl mx-auto container-padding">
          <motion.div className="text-center mb-16" {...fadeUp}>
            <h2 className="text-4xl font-extrabold text-slate-900 mb-4">What Students Say</h2>
            <p className="text-lg text-slate-600">Real feedback from our learning community</p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                name: 'Sarah Johnson',
                role: 'Web Developer',
                text: 'Lumina transformed my career. The courses are comprehensive and the instructors are world-class.',
                image: 'https://images.unsplash.com/photo-1713865467253-ce0ac8477d34?crop=entropy&cs=srgb&fm=jpg&q=85',
              },
              {
                name: 'Michael Chen',
                role: 'UX Designer',
                text: 'Best investment I made in my education. The platform is intuitive and the content is exceptional.',
                image: 'https://images.unsplash.com/photo-1575299833801-85ce40813bac?crop=entropy&cs=srgb&fm=jpg&q=85',
              },
              {
                name: 'Emily Rodriguez',
                role: 'Marketing Manager',
                text: 'The flexibility to learn at my own pace while receiving quality education is unmatched.',
                image: 'https://images.pexels.com/photos/6147053/pexels-photo-6147053.jpeg',
              },
            ].map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-stone-50 rounded-3xl p-8 border border-stone-100"
              >
                <div className="flex gap-1 text-amber-500 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 fill-current" />
                  ))}
                </div>
                <p className="text-slate-700 mb-6 leading-relaxed">{testimonial.text}</p>
                <div className="flex items-center gap-4">
                  <img
                    src={testimonial.image}
                    alt={testimonial.name}
                    className="w-12 h-12 rounded-full object-cover"
                  />
                  <div>
                    <div className="font-semibold text-slate-900">{testimonial.name}</div>
                    <div className="text-sm text-slate-600">{testimonial.role}</div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-gradient-to-br from-blue-600 to-blue-700">
        <div className="max-w-4xl mx-auto container-padding text-center">
          <motion.div {...fadeUp}>
            <h2 className="text-4xl font-extrabold text-white mb-6">Ready to Start Learning?</h2>
            <p className="text-xl text-blue-100 mb-8">
              Join thousands of students and start your learning journey today
            </p>
            <Link to="/register" data-testid="cta-signup-button">
              <button className="bg-white text-blue-600 hover:bg-blue-50 rounded-full px-10 py-5 text-lg font-semibold shadow-xl transition-all duration-300">
                Sign Up Now
              </button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}

function CourseCard({ course, index, compact = false }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.4, delay: index * 0.1 }}
    >
      <Link to={`/courses/${course.id}`} data-testid={`course-card-${course.id}`}>
        <div className="course-card h-full flex flex-col">
          <div className="relative overflow-hidden">
            <img
              src={course.thumbnail}
              alt={course.title}
              className="w-full aspect-video object-cover group-hover:scale-105 transition-transform duration-300"
            />
            {course.rating > 0 && (
              <div className="absolute top-4 right-4 bg-white rounded-full px-3 py-1 flex items-center gap-1 shadow-lg">
                <Star className="w-4 h-4 fill-amber-500 text-amber-500" />
                <span className="font-semibold text-sm">{course.rating}</span>
              </div>
            )}
          </div>
          <div className="p-6 flex-grow flex flex-col">
            <div className="flex items-center gap-2 mb-3">
              <span className="badge badge-blue">{course.level}</span>
              {course.students_count > 50 && (
                <span className="badge badge-amber">Best Seller</span>
              )}
            </div>
            <h3 className={`font-bold text-slate-900 mb-2 line-clamp-2 ${compact ? 'text-base' : 'text-xl'}`}>
              {course.title}
            </h3>
            {!compact && (
              <p className="text-slate-600 text-sm mb-4 line-clamp-2">{course.description}</p>
            )}
            <div className="flex items-center gap-3 text-sm text-slate-600 mb-4">
              <div className="flex items-center gap-1">
                <Users className="w-4 h-4" />
                <span>{course.students_count}</span>
              </div>
              <div className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                <span>{course.lessons?.length || 0} lessons</span>
              </div>
            </div>
            <div className="mt-auto flex items-center justify-between">
              <div className="text-2xl font-extrabold text-blue-600">${course.price}</div>
              <button className="text-blue-600 hover:text-blue-700 font-semibold flex items-center gap-1">
                Learn More <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </Link>
    </motion.div>
  );
}

export default HomePage;
